import React, { useEffect, useState, useMemo } from "react";
import {
  Container, Row, Col, Card, CardBody, Table, Badge, Spinner, Button, Input,
} from "reactstrap";
import { Link } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { getPortalsByOwner, getTicketsByOwner, getAllTenants } from "../../../helpers/firestoreService";
import { useAuthUser } from "../../../hooks/AuthProvider";
import homeBg from "../../../assets/images/home.png";

const SEVERITY_COLORS = {
  Low: "info",
  Medium: "warning",
  High: "danger",
  Urgent: "danger",
};

const STATUS_COLORS = {
  Open: "primary",
  "In Progress": "warning",
  Done: "success",
};

const CHART_SEVERITY_COLORS = {
  Low: "#0dcaf0",
  Medium: "#ffc107",
  High: "#fd7e14",
  Urgent: "#dc3545",
};

const CHART_STATUS_COLORS = {
  Open: "#556ee6",
  "In Progress": "#f1b44c",
  Done: "#34c38f",
  Resolved: "#34c38f",
  Closed: "#adb5bd",
};

const SupportDashboard = () => {
  document.title = "Dashboard | Porcher";

  const user = useAuthUser();
  const [portals,  setPortals]  = useState([]);
  const [tickets,  setTickets]  = useState([]);
  const [tenants,  setTenants]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [period,   setPeriod]   = useState("month");
  const [portalFilter, setPortalFilter] = useState(""); // "" = all

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const [p, t, ten] = await Promise.all([
          getPortalsByOwner(user.uid),
          getTicketsByOwner(user.uid),
          getAllTenants(user.uid),
        ]);
        setPortals(p);
        setTickets(t);
        setTenants(ten);
      } catch (e) {
        console.error("Dashboard load error:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  // ── Filters ─────────────────────────────────────────────────────────────────
  const getPeriodStart = (p) => {
    const d = new Date();
    if (p === "today") { d.setHours(0, 0, 0, 0); return d; }
    if (p === "week")  { d.setDate(d.getDate() - d.getDay()); d.setHours(0, 0, 0, 0); return d; }
    if (p === "month") { d.setDate(1); d.setHours(0, 0, 0, 0); return d; }
    if (p === "year")  { d.setMonth(0, 1); d.setHours(0, 0, 0, 0); return d; }
    return null;
  };

  const visibleTickets = useMemo(() => {
    const start = getPeriodStart(period);
    return tickets.filter((t) => {
      if (portalFilter && t.portalId !== portalFilter) return false;
      if (start && (!t.createdAt || t.createdAt < start)) return false;
      return true;
    });
  }, [tickets, period, portalFilter]);

  const visibleTenants = useMemo(() =>
    portalFilter ? tenants.filter((t) => t.portalId === portalFilter) : tenants,
    [tenants, portalFilter]
  );

  // ── Derived metrics ──────────────────────────────────────────────────────────
  const openTickets   = visibleTickets.filter((t) => t.status !== "Done" && t.status !== "Resolved" && t.status !== "Closed");
  const closedTickets = visibleTickets.filter((t) => t.status === "Done" || t.status === "Resolved" || t.status === "Closed");
  const urgentCount   = visibleTickets.filter((t) => t.severity === "Urgent" || t.severity === "High").length;

  // ── Chart data ───────────────────────────────────────────────────────────────
  // Tickets over time (last 30 days as daily buckets, or last 12 months)
  const timelineData = useMemo(() => {
    if (period === "today" || period === "week") {
      // hourly buckets for today, daily for week
      const buckets = {};
      const now = new Date();
      if (period === "today") {
        for (let h = 0; h < 24; h++) {
          const label = `${h.toString().padStart(2, "0")}:00`;
          buckets[label] = 0;
        }
        visibleTickets.forEach((t) => {
          if (t.createdAt) {
            const label = `${t.createdAt.getHours().toString().padStart(2, "0")}:00`;
            if (buckets[label] !== undefined) buckets[label]++;
          }
        });
      } else {
        // week — 7 day labels
        for (let i = 6; i >= 0; i--) {
          const d = new Date(now);
          d.setDate(d.getDate() - i);
          const label = d.toLocaleDateString("en-US", { weekday: "short" });
          buckets[label] = (buckets[label] || 0);
        }
        visibleTickets.forEach((t) => {
          if (t.createdAt) {
            const label = t.createdAt.toLocaleDateString("en-US", { weekday: "short" });
            if (buckets[label] !== undefined) buckets[label]++;
          }
        });
      }
      return Object.entries(buckets).map(([name, tickets]) => ({ name, tickets }));
    }

    if (period === "month") {
      const now = new Date();
      const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
      const buckets = {};
      for (let d = 1; d <= daysInMonth; d++) buckets[d] = 0;
      visibleTickets.forEach((t) => {
        if (t.createdAt) {
          const day = t.createdAt.getDate();
          if (buckets[day] !== undefined) buckets[day]++;
        }
      });
      return Object.entries(buckets).map(([name, tickets]) => ({ name: `${name}`, tickets }));
    }

    // year or all — monthly buckets
    const buckets = {};
    const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    if (period === "year") {
      months.forEach((m) => { buckets[m] = 0; });
      visibleTickets.forEach((t) => {
        if (t.createdAt) {
          const m = months[t.createdAt.getMonth()];
          buckets[m]++;
        }
      });
      return months.map((name) => ({ name, tickets: buckets[name] }));
    }
    // all time — group by "Mon YYYY"
    visibleTickets.forEach((t) => {
      if (t.createdAt) {
        const label = t.createdAt.toLocaleDateString("en-US", { month: "short", year: "numeric" });
        buckets[label] = (buckets[label] || 0) + 1;
      }
    });
    return Object.entries(buckets)
      .sort((a, b) => new Date(a[0]) - new Date(b[0]))
      .map(([name, tickets]) => ({ name, tickets }));
  }, [visibleTickets, period]);

  const severityPieData = useMemo(() =>
    Object.entries({ Low: 0, Medium: 0, High: 0, Urgent: 0 }).map(([name]) => {
      const value = visibleTickets.filter((t) => t.severity === name).length;
      return { name, value };
    }).filter((d) => d.value > 0),
    [visibleTickets]
  );

  const statusPieData = useMemo(() => {
    const counts = {};
    visibleTickets.forEach((t) => {
      counts[t.status] = (counts[t.status] || 0) + 1;
    });
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [visibleTickets]);

  const recentTen = visibleTickets.slice(0, 10);

  const copyShareLink = (shareId) => {
    const url = `${window.location.origin}/p/${shareId}`;
    navigator.clipboard.writeText(url);
  };

  if (loading) {
    return (
      <div className="page-content text-center py-5">
        <Spinner color="primary" />
      </div>
    );
  }

  const PERIOD_LABELS = {
    today: "Today", week: "This Week", month: "This Month", year: "This Year", all: "All Time",
  };

  return (
    <div className="page-content">
      <Container fluid>

        {/* ── Hero Banner ──────────────────────────────────────────────────── */}
        <div className="rounded mb-4 p-4 text-white position-relative overflow-hidden" style={{ minHeight: 180 }}>
          <div style={{ position: "absolute", inset: 0, backgroundImage: `url(${homeBg})`, backgroundSize: "cover", backgroundPosition: "center", opacity: 0.18 }} />
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg, #556ee6 0%, #34c38f 100%)" }} />
          <div className="position-relative" style={{ zIndex: 1 }}>
            <Row className="align-items-center">
              <Col>
                <h3 className="mb-1 text-white">Dashboard</h3>
                <p className="mb-0 opacity-75">
                  Welcome back{user?.displayName ? `, ${user.displayName}` : ""}. Here&apos;s what&apos;s happening with your portals.
                </p>
              </Col>
              <Col xs="auto" className="d-flex align-items-center gap-2">
                <Input
                  type="select"
                  bsSize="sm"
                  value={portalFilter}
                  onChange={(e) => setPortalFilter(e.target.value)}
                  style={{ width: 160 }}
                >
                  <option value="">All Portals</option>
                  {portals.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </Input>
                <Input
                  type="select"
                  bsSize="sm"
                  value={period}
                  onChange={(e) => setPeriod(e.target.value)}
                  style={{ width: 140 }}
                >
                  <option value="today">Today</option>
                  <option value="week">This Week</option>
                  <option value="month">This Month</option>
                  <option value="year">This Year</option>
                  <option value="all">All Time</option>
                </Input>
                <Link to="/portals">
                  <Button color="light" size="sm">
                    <i className="bx bx-plus me-1" />
                    New Portal
                  </Button>
                </Link>
              </Col>
            </Row>
            <Row className="mt-3">
              <Col xs="auto">
                <div className="d-flex align-items-center gap-2">
                  <Badge color="light" className="text-dark fs-6 px-3 py-2">
                    <i className="bx bx-globe me-1" />
                    {portals.length} Portal{portals.length !== 1 ? "s" : ""}
                  </Badge>
                  <Badge color="light" className="text-dark fs-6 px-3 py-2">
                    <i className="bx bx-task me-1" />
                    {visibleTickets.length} Ticket{visibleTickets.length !== 1 ? "s" : ""}
                  </Badge>
                  <Badge color="light" className="text-dark fs-6 px-3 py-2">
                    <i className="bx bx-user me-1" />
                    {visibleTenants.length} Tenant{visibleTenants.length !== 1 ? "s" : ""}
                  </Badge>
                </div>
              </Col>
            </Row>
          </div>
        </div>

        {/* ── Metric Cards ─────────────────────────────────────────────────── */}
        <Row className="g-3 mb-4">
          {[
            { icon: "bx bx-globe",        label: "Portals",          value: portals.length,          color: "primary",  to: "/portals"  },
            { icon: "bx bx-buildings",     label: "Tenants",          value: visibleTenants.length,   color: "secondary", to: "/tenants" },
            { icon: "bx bx-task",          label: "Open Tickets",     value: openTickets.length,      color: "warning",  to: "/tickets"  },
            { icon: "bx bx-check-circle",  label: "Closed Tickets",   value: closedTickets.length,    color: "success",  to: "/tickets"  },
            { icon: "bx bx-error-circle",  label: "High / Urgent",    value: urgentCount,             color: "danger",   to: "/tickets"  },
            { icon: "bx bx-trending-up",   label: `${PERIOD_LABELS[period]} Total`, value: visibleTickets.length, color: "info", to: "/tickets" },
          ].map(({ icon, label, value, color, to }) => (
            <Col xl={2} md={4} sm={6} key={label}>
              <Link to={to} className="text-decoration-none">
                <Card className="border-0 shadow-sm h-100">
                  <CardBody className="d-flex align-items-center gap-3 py-3">
                    <div
                      className={`rounded-circle bg-${color} bg-opacity-10 d-flex align-items-center justify-content-center flex-shrink-0`}
                      style={{ width: 46, height: 46 }}
                    >
                      <i className={`${icon} text-${color}`} style={{ fontSize: "1.3rem" }} />
                    </div>
                    <div className="overflow-hidden">
                      <p className="text-muted mb-0 small text-truncate">{label}</p>
                      <h3 className="mb-0 fw-bold">{value}</h3>
                    </div>
                  </CardBody>
                </Card>
              </Link>
            </Col>
          ))}
        </Row>

        {/* ── Charts Row ───────────────────────────────────────────────────── */}
        <Row className="g-3 mb-4">
          {/* Tickets over time */}
          <Col xl={8} lg={7}>
            <Card className="border-0 shadow-sm h-100">
              <CardBody>
                <h6 className="fw-semibold mb-3">
                  Tickets — {PERIOD_LABELS[period]}
                  {portalFilter && portals.find(p => p.id === portalFilter)
                    ? ` · ${portals.find(p => p.id === portalFilter).name}`
                    : ""}
                </h6>
                {timelineData.length === 0 || visibleTickets.length === 0 ? (
                  <div className="text-center text-muted py-4 small">No ticket data for this period.</div>
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={timelineData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="tickets" fill="#556ee6" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardBody>
            </Card>
          </Col>

          {/* Status breakdown pie */}
          <Col xl={4} lg={5}>
            <Card className="border-0 shadow-sm h-100">
              <CardBody>
                <h6 className="fw-semibold mb-3">Status Breakdown</h6>
                {statusPieData.length === 0 ? (
                  <div className="text-center text-muted py-4 small">No data for this period.</div>
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie
                        data={statusPieData}
                        cx="50%"
                        cy="45%"
                        innerRadius={55}
                        outerRadius={80}
                        dataKey="value"
                        paddingAngle={3}
                      >
                        {statusPieData.map((entry) => (
                          <Cell key={entry.name} fill={CHART_STATUS_COLORS[entry.name] || "#adb5bd"} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* ── Second Charts Row ────────────────────────────────────────────── */}
        <Row className="g-3 mb-4">
          {/* Severity pie */}
          <Col xl={4} lg={5}>
            <Card className="border-0 shadow-sm h-100">
              <CardBody>
                <h6 className="fw-semibold mb-3">Severity Breakdown</h6>
                {severityPieData.length === 0 ? (
                  <div className="text-center text-muted py-4 small">No data for this period.</div>
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie
                        data={severityPieData}
                        cx="50%"
                        cy="45%"
                        innerRadius={55}
                        outerRadius={80}
                        dataKey="value"
                        paddingAngle={3}
                      >
                        {severityPieData.map((entry) => (
                          <Cell key={entry.name} fill={CHART_SEVERITY_COLORS[entry.name] || "#adb5bd"} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </CardBody>
            </Card>
          </Col>

          {/* Portals panel */}
          <Col xl={8} lg={7}>
            <Card className="border-0 shadow-sm h-100">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0 fw-semibold">Portals</h6>
                  <Link to="/portals" className="text-primary small">Manage</Link>
                </div>
                {portals.length === 0 ? (
                  <div className="text-center py-4">
                    <i className="bx bx-globe text-muted" style={{ fontSize: "2rem" }} />
                    <p className="text-muted mt-2 mb-2 small">No portals yet.</p>
                    <Link to="/portals"><Button color="primary" size="sm" outline>Create one</Button></Link>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <Table className="table-hover mb-0" size="sm">
                      <thead>
                        <tr>
                          <th>Portal</th>
                          <th className="text-center">Tenants</th>
                          <th className="text-center">Open</th>
                          <th className="text-center">Auto-Reply</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {portals.map((p) => {
                          const pTickets = tickets.filter((t) => t.portalId === p.id && t.status !== "Done" && t.status !== "Resolved");
                          const pTenants = tenants.filter((t) => t.portalId === p.id);
                          return (
                            <tr key={p.id}>
                              <td>
                                <Link to={`/portals/${p.id}`} className="fw-semibold text-decoration-none">
                                  {p.name}
                                </Link>
                              </td>
                              <td className="text-center">{pTenants.length}</td>
                              <td className="text-center">
                                {pTickets.length > 0
                                  ? <Badge color="warning">{pTickets.length}</Badge>
                                  : <span className="text-muted">—</span>}
                              </td>
                              <td className="text-center">
                                <Badge color={p.autoResponseEnabled ? "success" : "secondary"} style={{ fontSize: "0.7rem" }}>
                                  {p.autoResponseEnabled ? "On" : "Off"}
                                </Badge>
                              </td>
                              <td>
                                <button
                                  className="btn btn-sm btn-outline-secondary py-0"
                                  onClick={() => copyShareLink(p.shareId)}
                                  title="Copy share link"
                                >
                                  <i className="bx bx-copy" />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* ── Recent Tickets ───────────────────────────────────────────────── */}
        <Row>
          <Col>
            <Card className="border-0 shadow-sm">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0 fw-semibold">Recent Tickets</h6>
                  <Link to="/tickets" className="text-primary small">View all</Link>
                </div>
                {recentTen.length === 0 ? (
                  <div className="text-center py-4">
                    <i className="bx bx-receipt text-muted" style={{ fontSize: "2rem" }} />
                    <p className="text-muted mt-2 mb-0 small">No tickets for this period / filter.</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <Table className="table-hover mb-0" size="sm">
                      <thead>
                        <tr>
                          <th>Title</th>
                          <th>Portal</th>
                          <th>Requester</th>
                          <th>Severity</th>
                          <th>Status</th>
                          <th>Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentTen.map((t) => (
                          <tr key={`${t.portalId}-${t.id}`}>
                            <td>
                              <Link to={`/tickets/${t.portalId}/${t.id}`}>{t.title}</Link>
                            </td>
                            <td className="text-muted small">{t.portalName}</td>
                            <td className="text-muted small">{t.requesterName || "—"}</td>
                            <td>
                              <Badge color={SEVERITY_COLORS[t.severity] || "secondary"} style={{ fontSize: "0.7rem" }}>
                                {t.severity}
                              </Badge>
                            </td>
                            <td>
                              <Badge color={STATUS_COLORS[t.status] || "secondary"} style={{ fontSize: "0.7rem" }}>
                                {t.status}
                              </Badge>
                            </td>
                            <td className="text-muted small">
                              {t.createdAt ? t.createdAt.toLocaleDateString() : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

      </Container>
    </div>
  );
};

export default SupportDashboard;
